import * as React from "react";
import { Container, Divider, Grid } from "semantic-ui-react";
import * as moment from "moment";
import { createValidator } from "not-valid";
import { useDispatch } from "react-redux";

import { AccordionQuestionnaireContainer } from "../../questionnaires/components/AccordionQuestionnaireContainer";
import {
    UpdateWorkAdjustmentQuestionnaireModel,
    UPDATE_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY,
    UpdateWorkAdjustmentQuestionnaireSections as sections,
    UpdateWorkAdjustmentQuestionnaireSectionTitles as sectionTitles,
    ParentReferralReportModel,
    shiftTypesOptions,
    daysPlannedForWorkOptions,
    reasonForClosureOptions,
    typeOfAppointmentRequestedOptions,
    workAdjustmentFormsAcceptedExtentions,
    DATE_FORMAT,
    getWorkAdjustmentReviewDate,
    isQuestionAnswerValid,
    isLessThanSixWeeksIntoTheFuture,
    isInTheFuture,
    ReasonForClosure
} from "../models";
import { AccordionQuestionnaireSection } from "../../questionnaires/components/AccordionQuestionnaireSection";
import { CheckboxQuestion } from "../../questionnaires/components/questions/CheckboxQuestion";
import { updateWorkAdjustmentQuestionnaireMasterConfig as masterConfig } from "../updateWorkAdjustmentQuestionnaireMasterConfig";
import * as questionIds from "../updateWorkAdjustmentQuestionnaireQuestionIds";
import { QuestionConfig, CustomQuestionConfig } from "../../questionnaires/questionnaireConfig";
import { WorkClassifications } from "../../referralReport/models";
import { OldDateQuestion } from "../../questionnaires/components/questions/OldDateQuestion";
import { DropdownQuestion } from "../../questionnaires/components/questions/DropdownQuestion";
import { MultiCheckboxQuestion, MultiCheckbox } from "../../questionnaires/components/questions/MultiCheckboxQuestion";
import { QuestionAnswer, AnsweredQuestion, QuestionnaireTypes } from "../../questionnaires/model";
import { TextQuestion } from "../../questionnaires/components/questions/TextQuestion";
import { RadioButtonQuestion, RadioButton, YesOrNo } from "../../questionnaires/components/questions/RadioButtonQuestion";
import { FilesQuestion } from "../../questionnaires/components/questions/FilesQuestion";
import { updateQuestion } from "../../questionnaires/actions";
import { TimeQuestion } from "../../questionnaires/components/questions/TimeQuestion";
import { ShareDocumentsMessageDto } from "../../shareDocuments/models";
import { SendDocumentsToAssociate } from "../../shareDocuments/components/SendDocumentsToAssociate";

interface OwnProps {
    readonly: boolean;
    ohCaseId: string;
    taskId: string;
    customerId: string;
    employeeRecordId: string;
    questionnaire: UpdateWorkAdjustmentQuestionnaireModel;
    parentReferralReport: ParentReferralReportModel;
    notesFromMeetingApiUrl: string;
    recommendedEndDateOfAdjustment: string;
    fileApiUrl: string;
    onSendDocumentsToAssociate: (model: ShareDocumentsMessageDto) => Promise<void>;
}

interface DispatchProps {
    loadQuestionnaire: () => void;
    submitQuestionnaire?: (questionnaire: UpdateWorkAdjustmentQuestionnaireModel) => Promise<boolean>;
}

type AllProps = OwnProps & DispatchProps;

const UpdateWorkAdjustmentQuestionnaire: React.FunctionComponent<AllProps> = (props: AllProps) => {
    const dispatch = useDispatch();

    const loadQuestionnaire = async () => {
        await props.loadQuestionnaire();
    };

    const getQuestionConfig = (questionId: string): QuestionConfig => {
        return masterConfig.find(c => c.id === questionId) || { id: questionId, disabled: true };
    };

    const getAdjustmentDetailsSectionQuestionConfig = (questionId: string): QuestionConfig => {
        const questionText = masterConfig.find(c => questionId.includes(c.id)).questionText;
        const config: QuestionConfig = {
            id: questionId,
            questionText
        };
        return config;
    };

    const getAnswerToQuestionWithId = (id: string): QuestionAnswer => {
        return props.questionnaire?.questions?.find(question => question.id === id)?.answer;
    };

    const updateAnswer = (id: string, question: AnsweredQuestion, questionnaireKey: string, sectionId: string) => {
        dispatch(updateQuestion({ id, question, questionnaireKey, sectionId }));
    };

    const setAnswer = (sectionId: string, questionId: string, answer: string, questionConfigId?: string) => {
        const questionConfig = masterConfig.find(q => q.id === (questionConfigId || questionId)) as CustomQuestionConfig;
        updateAnswer(
            questionId,
            { question: questionConfig.questionText, sectionId, id: questionId, answer, isValid: true },
            UPDATE_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY,
            sectionId
        );
    };

    const setDefaultRecommendedEndDate = (questionId: string) => {
        if (props.recommendedEndDateOfAdjustment && moment(props.recommendedEndDateOfAdjustment)) {
            setAnswer(
                sections.NewAdjustmentDetails,
                questionId,
                props.recommendedEndDateOfAdjustment
            );
        }
    };

    const isEmployeeNotAtWork =
        props.parentReferralReport?.workClassification === WorkClassifications.NotAtWorkMedicalIssue ||
        props.parentReferralReport?.workClassification === WorkClassifications.NotAtWorkManagementIssue;
    const isAssociateReadyToReturnToNormalWorkingAnswer = getAnswerToQuestionWithId(questionIds.IsAssociateReadyToReturnToNormalWorking);
    const hasAnExtendedWorkAdjustmentBeenAgreedAnswer = getAnswerToQuestionWithId(questionIds.HasAnExtendedWorkAdjustmentBeenAgreed);
    const shouldThisWorkAdjustmentCaseBeClosed = getAnswerToQuestionWithId(questionIds.CloseThisWorkAdjustmentCase) === YesOrNo.Yes;
    const confirmYouWishToRequestReReferralAnswer = getAnswerToQuestionWithId(questionIds.ConfirmYouWishToRequestReReferral);
    const endDateOfAdjustmentAnswer = getAnswerToQuestionWithId(questionIds.EndDateOfAdjustment0) as string;
    const isEndDateOfAdjustmentAnswerValid = isQuestionAnswerValid(questionIds.EndDateOfAdjustment0, props.questionnaire);
    const isReasonForClosureLeftEmployment = getAnswerToQuestionWithId(questionIds.ReasonForClosure) === ReasonForClosure.LeftEmployment;

    const shouldShowAdjustmentSection1 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment0) === YesOrNo.No;
    const shouldShowAdjustmentSection2 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment1) === YesOrNo.No;
    const shouldShowAdjustmentSection3 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment2) === YesOrNo.No;
    const shouldShowAdjustmentSection4 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment3) === YesOrNo.No;
    const shouldShowAdjustmentSection5 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment4) === YesOrNo.No;
    const shouldShowAdjustmentSection6 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment5) === YesOrNo.No;
    const shouldShowAdjustmentSection7 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment6) === YesOrNo.No;
    const shouldShowAdjustmentSection8 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment7) === YesOrNo.No;
    const shouldShowAdjustmentSection9 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment8) === YesOrNo.No;
    const shouldShowAdjustmentSection10 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment9) === YesOrNo.No;
    const shouldShowAdjustmentSection11 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment10) === YesOrNo.No;
    const shouldShowAdjustmentSection12 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment11) === YesOrNo.No;
    const shouldShowAdjustmentSection13 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment12) === YesOrNo.No;
    const shouldShowAdjustmentSection14 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment13) === YesOrNo.No;
    const shouldShowAdjustmentSection15 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment14) === YesOrNo.No;
    const shouldShowAdjustmentSection16 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment15) === YesOrNo.No;
    const shouldShowAdjustmentSection17 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment16) === YesOrNo.No;
    const shouldShowAdjustmentSection18 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment17) === YesOrNo.No;
    const shouldShowAdjustmentSection19 = getAnswerToQuestionWithId(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment18) === YesOrNo.No;

    React.useEffect(() => {
        if (isEndDateOfAdjustmentAnswerValid) {
            const newWorkAdjustmentReviewDate = getWorkAdjustmentReviewDate(endDateOfAdjustmentAnswer);
            setAnswer(
                sections.NewAdjustmentDetails,
                questionIds.WorkAdjustmentReviewDate,
                newWorkAdjustmentReviewDate
            );
        }
    }, [isEndDateOfAdjustmentAnswerValid, endDateOfAdjustmentAnswer]);

    React.useEffect(() => {
        if (isAssociateReadyToReturnToNormalWorkingAnswer === YesOrNo.Yes) {
            setAnswer(
                sections.ClosureOfAdjustments,
                questionIds.CloseThisWorkAdjustmentCase,
                YesOrNo.Yes
            );
            const defaultDateOfClosure = moment().format(DATE_FORMAT);
            setAnswer(
                sections.ClosureOfAdjustments,
                questionIds.DateOfClosure,
                defaultDateOfClosure
            );
        }
    }, [isAssociateReadyToReturnToNormalWorkingAnswer]);

    React.useEffect(() => {
        if (hasAnExtendedWorkAdjustmentBeenAgreedAnswer === YesOrNo.No) {
            setAnswer(
                sections.ReReferral,
                questionIds.ConfirmYouWishToRequestReReferral,
                YesOrNo.Yes
            );
        }
    }, [hasAnExtendedWorkAdjustmentBeenAgreedAnswer]);

    React.useEffect(() => {
        if (hasAnExtendedWorkAdjustmentBeenAgreedAnswer === YesOrNo.Yes) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment0);
        }
    }, [hasAnExtendedWorkAdjustmentBeenAgreedAnswer]);

    React.useEffect(() => {
        if (shouldShowAdjustmentSection1) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment1);
        }
        if (shouldShowAdjustmentSection2) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment2);
        }
        if (shouldShowAdjustmentSection3) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment3);
        }
        if (shouldShowAdjustmentSection4) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment4);
        }
        if (shouldShowAdjustmentSection5) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment5);
        }
        if (shouldShowAdjustmentSection6) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment6);
        }
        if (shouldShowAdjustmentSection7) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment7);
        }
        if (shouldShowAdjustmentSection8) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment8);
        }
        if (shouldShowAdjustmentSection9) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment9);
        }
        if (shouldShowAdjustmentSection10) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment10);
        }
        if (shouldShowAdjustmentSection11) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment11);
        }
        if (shouldShowAdjustmentSection12) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment12);
        }
        if (shouldShowAdjustmentSection13) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment13);
        }
        if (shouldShowAdjustmentSection14) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment14);
        }
        if (shouldShowAdjustmentSection15) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment15);
        }
        if (shouldShowAdjustmentSection16) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment16);
        }
        if (shouldShowAdjustmentSection17) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment17);
        }
        if (shouldShowAdjustmentSection18) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment18);
        }
        if (shouldShowAdjustmentSection19) {
            setDefaultRecommendedEndDate(questionIds.EndDateOfAdjustment19);
        }
    }, [shouldShowAdjustmentSection1,
        shouldShowAdjustmentSection2,
        shouldShowAdjustmentSection3,
        shouldShowAdjustmentSection4,
        shouldShowAdjustmentSection5,
        shouldShowAdjustmentSection6,
        shouldShowAdjustmentSection7,
        shouldShowAdjustmentSection8,
        shouldShowAdjustmentSection9,
        shouldShowAdjustmentSection10,
        shouldShowAdjustmentSection11,
        shouldShowAdjustmentSection12,
        shouldShowAdjustmentSection13,
        shouldShowAdjustmentSection14,
        shouldShowAdjustmentSection15,
        shouldShowAdjustmentSection16,
        shouldShowAdjustmentSection17,
        shouldShowAdjustmentSection18,
        shouldShowAdjustmentSection19]);

    return (
        <Container className="referral-report">
            {!props.readonly &&
                <Grid>
                    <Grid.Column textAlign="right">
                        <SendDocumentsToAssociate
                            employeeRecordId={props.employeeRecordId}
                            fileApiUrl={props.fileApiUrl}
                            onSubmit={props.onSendDocumentsToAssociate}
                        />
                    </Grid.Column>
                </Grid>
            }
            <AccordionQuestionnaireContainer
                loadQuestionnaire={loadQuestionnaire}
                submitQuestionnaire={(questions) => props.submitQuestionnaire({ questions, eTag: props.questionnaire.eTag })}
                readonly={props.readonly}
                questionnaireKey={UPDATE_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY}
                questionnaireType={QuestionnaireTypes.Other}
            >
                <AccordionQuestionnaireSection
                    title={sectionTitles.InformationFromOhReport}
                    id={sections.InformationFromOhReport}
                    hideNextButton={true}
                    active={true}
                >
                    <TextQuestion
                        inline
                        questionConfig={getQuestionConfig(questionIds.HrManager)}
                        answer={props.parentReferralReport?.hrManager}
                        readonly={true}
                    />
                    <TextQuestion
                        inline
                        questionConfig={getQuestionConfig(questionIds.EmployeeSeen)}
                        answer={props.parentReferralReport?.employeeSeen}
                        readonly={true}
                    />
                    <OldDateQuestion
                        inline
                        questionConfig={getQuestionConfig(questionIds.DateOfBirth)}
                        answer={props.parentReferralReport?.dateOfBirth?.format(DATE_FORMAT)}
                        readonly={true}
                    />
                    <TextQuestion
                        inline
                        questionConfig={getQuestionConfig(questionIds.EmployeeId)}
                        answer={props.parentReferralReport?.employeeUniqueId}
                        readonly={true}
                    />
                    <TextQuestion
                        inline
                        questionConfig={getQuestionConfig(questionIds.EmployerAndDivision)}
                        answer={props.parentReferralReport?.employerAndDivision}
                        readonly={true}
                    />
                    <TextQuestion
                        hideDivider
                        inline
                        questionConfig={getQuestionConfig(questionIds.JobTitle)}
                        answer={props.parentReferralReport?.jobTitle}
                        readonly={true}
                    />
                </AccordionQuestionnaireSection>
                <AccordionQuestionnaireSection
                    title={sectionTitles.ReviewOfAdjustments}
                    id={sections.ReviewOfAdjustments}
                    hideNextButton={true}
                    active={true}
                >
                    <FilesQuestion
                        inline={props.readonly}
                        api={props.notesFromMeetingApiUrl}
                        questionConfig={getQuestionConfig(questionIds.NotesFromMeetingUploaded)}
                        readonly={props.readonly}
                        mandatory={!isReasonForClosureLeftEmployment}
                        showError={true}
                        acceptExtensions={workAdjustmentFormsAcceptedExtentions}
                    />
                    <OldDateQuestion
                        inline={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.DateOfMeeting)}
                        readonly={props.readonly}
                        mandatory={!isReasonForClosureLeftEmployment}
                    />
                    <RadioButtonQuestion
                        hideDivider
                        inline={props.readonly}
                        questionConfig={getQuestionConfig(questionIds.IsAssociateReadyToReturnToNormalWorking)}
                        readonly={props.readonly}
                        mandatory={true}
                    >
                        <RadioButton label="Yes" value={YesOrNo.Yes} />
                        <RadioButton label="No" value={YesOrNo.No} />
                    </RadioButtonQuestion>
                    {isAssociateReadyToReturnToNormalWorkingAnswer === YesOrNo.No &&
                        <>
                            <Divider />
                            <RadioButtonQuestion
                                hideDivider
                                inline={props.readonly}
                                questionConfig={getQuestionConfig(questionIds.HasAnExtendedWorkAdjustmentBeenAgreed)}
                                readonly={props.readonly}
                                mandatory={true}
                            >
                                <RadioButton label="Yes" value={YesOrNo.Yes} />
                                <RadioButton label="No" value={YesOrNo.No} />
                            </RadioButtonQuestion>
                        </>
                    }
                </AccordionQuestionnaireSection>
                {hasAnExtendedWorkAdjustmentBeenAgreedAnswer === YesOrNo.Yes &&
                    <AccordionQuestionnaireSection
                        title={sectionTitles.NewAdjustmentDetails}
                        id={sections.NewAdjustmentDetails}
                        hideNextButton={true}
                        active={true}
                    >
                        <OldDateQuestion
                            inline={props.readonly}
                            questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment0)}
                            readonly={props.readonly}
                            mandatory={true}
                        />
                        <OldDateQuestion
                            inline={props.readonly}
                            questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment0)}
                            readonly={props.readonly}
                            mandatory={true}
                        />
                        <DropdownQuestion
                            inline={props.readonly}
                            questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType0)}
                            options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                            readonly={props.readonly}
                            mandatory={true}
                        />
                        <MultiCheckboxQuestion
                            inline={props.readonly}
                            questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork0)}
                            readonly={props.readonly}
                            mandatory={true}
                            options={daysPlannedForWorkOptions}
                        >
                            {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                        </MultiCheckboxQuestion>
                        <TimeQuestion
                            inline={props.readonly}
                            questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime0)}
                            readonly={props.readonly}
                            mandatory={true}
                        />
                        <TimeQuestion
                            inline={props.readonly}
                            questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime0)}
                            readonly={props.readonly}
                            mandatory={true}
                        />
                        <RadioButtonQuestion
                            inline={props.readonly}
                            questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment0)}
                            readonly={props.readonly}
                            mandatory={true}
                        >
                            <RadioButton label="Yes" value={YesOrNo.Yes} />
                            <RadioButton label="No" value={YesOrNo.No} />
                        </RadioButtonQuestion>
                        {shouldShowAdjustmentSection1 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment1)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment1)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType1)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork1)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime1)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime1)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment1)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection2 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment2)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment2)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType2)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork2)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime2)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime2)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment2)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection3 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment3)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment3)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType3)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork3)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime3)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime3)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment3)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection4 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment4)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment4)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType4)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork4)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime4)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime4)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment4)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection5 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment5)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment5)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType5)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork5)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime5)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime5)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment5)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection6 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment6)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment6)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType6)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork6)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime6)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime6)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment6)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection7 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment7)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment7)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType7)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork7)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime7)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime7)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment7)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection8 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment8)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment8)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType8)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork8)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime8)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime8)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment8)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection9 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment9)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment9)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType9)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork9)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime9)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime9)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment9)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection10 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment10)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment10)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType10)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork10)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime10)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime10)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment10)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection11 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment11)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment11)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType11)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork11)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime11)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime11)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment11)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection12 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment12)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment12)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType12)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork12)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime12)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime12)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment12)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection13 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment13)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment13)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType13)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork13)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime13)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime13)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment13)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection14 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment14)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment14)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType14)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork14)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime14)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime14)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment14)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection15 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment15)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment15)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType15)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork15)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime15)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime15)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment15)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection16 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment16)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment16)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType16)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork16)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime16)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime16)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment16)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection17 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment17)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment17)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType17)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork17)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime17)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime17)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment17)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection18 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment18)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment18)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType18)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork18)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime18)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime18)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <RadioButtonQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.IsThisPatternTheSameForTheWholeWorkAdjustment18)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                >
                                    <RadioButton label="Yes" value={YesOrNo.Yes} />
                                    <RadioButton label="No" value={YesOrNo.No} />
                                </RadioButtonQuestion>
                            </>
                        }
                        {shouldShowAdjustmentSection19 &&
                            <>
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartDateOfAdjustment19)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndDateOfAdjustment19)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.ShiftType19)}
                                    options={shiftTypesOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <MultiCheckboxQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.DaysPlannedForWork19)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    options={daysPlannedForWorkOptions}
                                >
                                    {(Object.keys(daysPlannedForWorkOptions) as (keyof typeof daysPlannedForWorkOptions)[]).map(k =>
                                        <MultiCheckbox key={k} value={k} label={daysPlannedForWorkOptions[k]} />)}
                                </MultiCheckboxQuestion>
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.StartTime19)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TimeQuestion
                                    inline={props.readonly}
                                    questionConfig={getAdjustmentDetailsSectionQuestionConfig(questionIds.EndTime19)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                            </>
                        }
                        {isEmployeeNotAtWork &&
                            <OldDateQuestion
                                inline={props.readonly}
                                questionConfig={getQuestionConfig(questionIds.ExpectedReturnToWorkDateIfKnown)}
                                readonly={props.readonly}
                                mandatory={false}
                            />
                        }
                        <OldDateQuestion
                            hideDivider
                            inline={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.WorkAdjustmentReviewDate)}
                            readonly={props.readonly}
                            mandatory={true}
                            validators={[
                                createValidator((value: moment.Moment) =>
                                    isLessThanSixWeeksIntoTheFuture(value), "Cannot be more than 6 weeks in the future"),
                                createValidator((value: moment.Moment) =>
                                    isInTheFuture(value), "Must be in the future")
                            ]}
                            showError={true && !props.readonly}
                        />
                    </AccordionQuestionnaireSection>
                }
                {isAssociateReadyToReturnToNormalWorkingAnswer === YesOrNo.Yes &&
                    <AccordionQuestionnaireSection
                        title={sectionTitles.ClosureOfAdjustments}
                        id={sections.ClosureOfAdjustments}
                        hideNextButton={true}
                        active={true}
                    >
                        <CheckboxQuestion
                            hideDivider
                            inline={props.readonly}
                            questionConfig={getQuestionConfig(questionIds.CloseThisWorkAdjustmentCase)}
                            value={YesOrNo.Yes}
                            readonly={true}
                        />
                        {shouldThisWorkAdjustmentCaseBeClosed &&
                            <>
                                <Divider />
                                <DropdownQuestion
                                    inline={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.ReasonForClosure)}
                                    options={reasonForClosureOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <OldDateQuestion
                                    inline={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.DateOfClosure)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                                <TextQuestion
                                    hideDivider
                                    inline={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.NotesFromClosure)}
                                    readonly={props.readonly}
                                    mandatory={false}
                                    multiline
                                />
                            </>
                        }
                    </AccordionQuestionnaireSection>
                }
                {hasAnExtendedWorkAdjustmentBeenAgreedAnswer === YesOrNo.No &&
                    <AccordionQuestionnaireSection
                        title={sectionTitles.ReReferral}
                        id={sections.ReReferral}
                        hideNextButton={true}
                        active={true}
                    >
                        <CheckboxQuestion
                            hideDivider
                            questionConfig={getQuestionConfig(questionIds.ConfirmYouWishToRequestReReferral)}
                            value={YesOrNo.Yes}
                            readonly={true}
                        />
                        {confirmYouWishToRequestReReferralAnswer === YesOrNo.Yes &&
                            <>
                                <Divider />
                                <TextQuestion
                                    inline={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.ReReferralDetails)}
                                    readonly={props.readonly}
                                    mandatory={true}
                                    multiline
                                />
                                <DropdownQuestion
                                    hideDivider
                                    inline={props.readonly}
                                    questionConfig={getQuestionConfig(questionIds.TypeOfAppointmentRequested)}
                                    options={typeOfAppointmentRequestedOptions.sort((a, b) => a.text.localeCompare(b.text))}
                                    readonly={props.readonly}
                                    mandatory={true}
                                />
                            </>
                        }
                    </AccordionQuestionnaireSection>
                }
            </AccordionQuestionnaireContainer>
        </Container>
    );
};

export { UpdateWorkAdjustmentQuestionnaire };
